import React, {useState} from 'react'
import {Block, Col, Row} from 'jsxstyle'
import {XpertKnowledgeDto, XpertKnowledgeStatus} from '../../../services/XpertKnowledge/type'
import {XpertKnowledgeTutorialStatus} from '../../../services/XpertKnowledgeTutorial/type'
import Loader from '../../../components/Loader'
import {ReactComponent as ErrorIcon} from '../../../common/assets/delete-icon.svg'
import {ReactComponent as DeleteFillIcon} from '../../../common/assets/delete-fill-icon.svg'
import {useXpertKnowledgeContext} from '../XpertKnowledgeContext'
import {useXpertKnowledgeAuthContext} from '../../../components/AuthContext'
import {UserRole} from '../../../services/User/type'
import i18n from '../../../common/i18n'
import QuickConfirmDialog from '../../../components/QuickConfirmDialog'
import {useNavigate} from 'react-router-dom'

interface StatusInfo {
  label: string
  color: string
  backgroundColor: string
  isLoading?: boolean
  isError?: boolean
}

export const statusConfig: Record<string, StatusInfo> = {
  [XpertKnowledgeTutorialStatus.DRAFT]: {
    label: i18n.t('xk.list.unpublished.status.draft'),
    color: '#AD5400',
    backgroundColor: '#FFF0E3',
  },
  [XpertKnowledgeTutorialStatus.IN_REVIEW]: {
    label: i18n.t('xk.list.unpublished.status.inReview'),
    color: '#AD5400',
    backgroundColor: '#FFF0E3',
  },
  [XpertKnowledgeTutorialStatus.REFUSED]: {
    label: i18n.t('xk.list.unpublished.status.refused'),
    color: '#AD5400',
    backgroundColor: '#FFF0E3',
  },
  [XpertKnowledgeTutorialStatus.PUBLISHED]: {
    label: i18n.t('xk.list.unpublished.status.published'),
    color: '#0E78F5',
    backgroundColor: '#E5F5FF',
  },
  [XpertKnowledgeStatus.WAITING_FOR_FILE]: {
    label: i18n.t('xk.list.unpublished.status.waitingFile'),
    color: '#666666',
    backgroundColor: '#F0F0F0',
    isLoading: true,
  },
  [XpertKnowledgeStatus.IN_GENERATION]: {
    label: i18n.t('xk.list.unpublished.status.generating'),
    color: '#666666',
    backgroundColor: '#F0F0F0',
    isLoading: true,
  },
  [XpertKnowledgeStatus.FAILED_GENERATION]: {
    label: i18n.t('xk.list.unpublished.status.failedGeneration'),
    color: '#AB0D0D',
    backgroundColor: '#FFC4C4',
    isError: true,
  },
  [XpertKnowledgeStatus.FAILED_RECEIVE_FILE]: {
    label: i18n.t('xk.list.unpublished.status.failedReceive'),
    color: '#AB0D0D',
    backgroundColor: '#FFC4C4',
    isError: true,
  },
  [XpertKnowledgeStatus.FAILED_EMPTY_TRANSCRIPT]: {
    label: i18n.t('xk.list.unpublished.status.failedTranscript'),
    color: '#AB0D0D',
    backgroundColor: '#FFC4C4',
    isError: true,
  },
}

export interface XpertKnowledgeUnPlublishedBlockProps {
  xpertKnowledge: XpertKnowledgeDto & {isSelected?: boolean}
  onClick?: () => void
}

const XpertKnowledgeUnPlublishedBlock: React.FC<XpertKnowledgeUnPlublishedBlockProps> = ({xpertKnowledge, onClick}) => {
  const {user} = useXpertKnowledgeAuthContext()
  const {hideXpertKnowledgeBuffer, deleteXpertKnowledge} = useXpertKnowledgeContext()
  const navigate = useNavigate()

  const status = xpertKnowledge.status
  const isGenerationComplete = status === XpertKnowledgeStatus.DONE
  const rawStatus = !isGenerationComplete ? status : xpertKnowledge.publishTutorial?.status ?? XpertKnowledgeTutorialStatus.DRAFT
  const isFailedBufferStatus = [XpertKnowledgeStatus.FAILED_RECEIVE_FILE, XpertKnowledgeStatus.FAILED_EMPTY_TRANSCRIPT].includes(status)
  const isFailedGenerationStatus = status === XpertKnowledgeStatus.FAILED_GENERATION

  const cfg = statusConfig[rawStatus] ?? statusConfig[XpertKnowledgeTutorialStatus.DRAFT]

  const handleHideBuffer = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (xpertKnowledge.reference) {
      hideXpertKnowledgeBuffer(xpertKnowledge.reference)
    }
  }

  const handleDeleteXpertKnowledge = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (xpertKnowledge.id) {
      deleteXpertKnowledge(xpertKnowledge.id)
    }
  }

  return (
    <Col
      cursor={isGenerationComplete ? 'pointer' : 'not-allowed'}
      props={{
        onClick: () => {
          isGenerationComplete && onClick?.()
        },
      }}
      width="320px"
      minWidth="320px"
      padding="16px"
      borderRadius="8px"
      boxSizing="border-box"
      backgroundColor="#FFFFFF"
      hoverBackgroundColor={isGenerationComplete ? '#FAF9FF' : undefined}
      border="1px solid #DAE4F4"
      boxShadow="0px 2px 4px 0px #0000001A"
      userSelect="none"
      position="relative"
    >
      {(isFailedBufferStatus || isFailedGenerationStatus) && user?.role != UserRole.AGENT && (
        <Block
          position="absolute"
          top="8px"
          right="8px"
          cursor="pointer"
          props={{
            onClick: isFailedBufferStatus ? handleHideBuffer : handleDeleteXpertKnowledge,
          }}
        >
          <DeleteFillIcon width={16} height={16} />
        </Block>
      )}

      <Block
        fontSize="16px"
        fontWeight={500}
        lineHeight="21px"
        marginBottom="12px"
        overflow="hidden"
        whiteSpace="nowrap"
        textOverflow="ellipsis"
        color={cfg.isLoading || cfg.isError ? '#888888' : '#000000'}
      >
        {xpertKnowledge.publishTutorial?.title ?? i18n.t('xk.list.unpublished.untitled')}
      </Block>

      <Row alignItems="center" justifyContent="space-between" width="100%">
        <Row
          alignItems="center"
          gap={cfg.isLoading || cfg.isError ? '6px' : undefined}
          padding="4px 12px"
          fontSize="12px"
          width="fit-content"
          height="30px"
          fontWeight={300}
          borderRadius="50px"
          backgroundColor={cfg.backgroundColor}
          color={cfg.color}
        >
          {cfg.isLoading && <Loader size={8} />}
          {cfg.isError && <ErrorIcon width={10} height={10} />}
          {cfg.label}
        </Row>
      </Row>
    </Col>
  )
}

export default XpertKnowledgeUnPlublishedBlock
